import { Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { GenericUtils } from '@app/app.commonutils';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { Place } from '@app/modules/model/place';
import { Offer } from '@app/modules/model/offer';
import { Extra } from '@app/modules/model/extra';
import { PrintDevice } from '@app/modules/model/printer';
import { Product } from '@app/modules/model/product';

import { OfferProduct } from '@app/modules/model/offerproduct';
import { OfferProductOption } from '@app/modules/model/offeroption';
import { ExtraProduct } from '@app/modules/model/extraproduct';
import { ExtraProductOption } from '@app/modules/model/extraoption';
import { PrintProduct } from '@app/modules/model/printproduct';
import { PrintProductOption } from '@app/modules/model/printoption';
import { ProductSelect } from '@app/modules/model/productselect';

type SuitableItems = Product | ProductSelect | OfferProduct | ExtraProduct | PrintProduct;
class SelectedItem {
    constructor(public item: SuitableItems){
        // nothing to do
    }

    get product(){
        return (this.item instanceof Product) ? this.item : this.item.product;
    }

    get direct(){
        if (this.item instanceof ProductSelect){
            return this.item;
        }
        // Un PrintProduct puede envolver un atajo (item.select); lo exponemos como
        // 'direct' para que el de-dup y el pintado de marcados funcionen igual.
        return ('select' in this.item) ? (this.item as any).select : null;
    }

    get options(){
        return ('options' in this.item) ? this.item.options : null;
    }
}

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-product-select',
    templateUrl: 'ui-select.html',
    styleUrls: [
      'ui-select.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiProductSelect extends languageSupport implements OnInit, OnChanges, AfterViewInit, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('group') _family: Product = null;
    @Input('offer') _offer: Offer = null; 
    @Input('extra') _extra: Extra = null;
    @Input('print') _print: PrintDevice = null;
    @Input('products') _products: Array <SuitableItems> = [];

    private _selected: Set <SelectedItem> = null;
    private _pristine: boolean = true;

    normalize(str){
        return GenericUtils.Normalize(str);
    }

    IsChecked(_product : Product | ProductSelect){
        for(let _item of this._selected){
            if ((_item.product == _product) || (_item.direct == _product)){
                return true;
            }
        } 

        return false;
    }

    get SelectedType(){
        if (!this._selected){
            return 'none';
        }

        switch(this._selected.size){
            case 0: return 'none';
            // un atajo (sin product) cuenta como 'product' para la UI.
            case 1: {
                let _only = [...this._selected][0];
                return (_only.product && _only.product.isgroup) ? 'group' : 'product';
            }
        }

        return 'custom';    // more than one item
    }

    get SelectedName(){
        if (this._selected.size == 1){
            let _only = [...this._selected][0];
            return _only.product ? _only.product.name : (_only.direct ? _only.direct.name : null);
        }
        return null;
    }

    private IsActive(product){
        if (product.IsPurchased){
            if (product.isgroup){
                for(let _product of this._place.products){
                    if ((!_product.isgroup) && _product.IsPurchased && _product.IsChild(product)){
                        return true;    // at least one active child for this group
                    }
                }
            }
            else {
                return true;
            }
        }
        return false;
    }

    private _activeproducts = null;
    get ActiveProducts(){
        if (this._activeproducts == null){
            let _products = [];
            if (this._place){
                for(let _product of this._place.products){
                    if (_product.IsFamily){
                        continue;   // do not display family products
                    }

                    if (this._offer){
                        if (_product.IsGeneric){
                            continue;   // do not display generic products for offers
                        }

                        if (_product.MarketPrice){
                            continue;   // do not display market price products for offers
                        }

                        if (_product.IsWeighted){
                            continue;   // do not display sold weighted products for offers
                        }
                    }

                    if (this.IsActive(_product)){
                        _products.push(_product);
                    }
                }
            }
            this._activeproducts = _products;
        }

        return this._activeproducts;
    }

    private _viewproducts = null;
    get ViewProducts() {;
        if (this._viewproducts == null){
            let _products = [];
            for(let _product of this.ActiveProducts){
                if (_product.parent == this._parent[0].product){
                    _products.push(_product);
                }
            }

            this._viewproducts = _products;
        }

        return this._viewproducts;        
    }

    private _selectedproduct = null;
    get Selected(): Product {
        return this._selectedproduct;
    }

    set Selected(value: Product){
        if (value != this._selectedproduct){
            this._selectedproduct = value;

            if (this._selectedproduct){
                // create the selected options set (based on IsSelectAll value)
                for (let _category of this._selectedproduct.categories){
                    for (let _option of _category.options){
                        this._selectedoptions.set(_option, this.IsSelectAll);
                    }
                }    

                // always enable the selected options provided in the input
                let _productoptions = null;
                for (let _item of this._selected){
                    if (_item.product == this._selectedproduct){
                        _productoptions = _item.options;
                    }
                }

                if (_productoptions){
                    for(let _option of _productoptions){
                        this._selectedoptions.set(_option.prodopt, _option.IsValid);
                    }
                }
            }

            this.change.markForCheck();
        }
    }    

    /**********************************/
    /* PRODUCT SELECTION              */
    /**********************************/
        
    @ViewChild('container', {static: false}) _container: ElementRef;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _RefreshPlace(){
        this._activeproducts = null;
        this._viewproducts = null;

        this.change.markForCheck();
    }

    private _place_subscription = null;

    ngOnInit(){
        this._place_subscription = this._place.OnTargetRefresh('PRODUCT').subscribe(
        data => {
            this._RefreshPlace();
        });
    }

    ngOnChanges(){
        this._selectall = false;
        this._selected = new Set <SelectedItem> ();

        if (this._products){
            for (let _item of this._products){
                this._selected.add(new SelectedItem(_item));
            }
        }

        this.change.markForCheck();
    }

    ngAfterViewInit(){
        this.change.detectChanges();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._place_subscription != null){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }
    }

    @Output('onAccept') _onAccept = new EventEmitter<any>();     
    OnAccept(){
        this._products.length = 0;
        for (let _product of this._selected){
            this._products.push(_product.item);
        }
        
        this._onAccept.emit(this._products);
        this.OnHome();
    }

    @Output('onCancel') _onCancel = new EventEmitter<any>();     
    async OnCancel(){
        console.log("[EDIT PRODUCTS] confirm changes lost");
        if (!this._pristine){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._onCancel.emit();
                    this.OnHome();
                }
            });

            await alert.present();
        }
        else {
            this._onCancel.emit();
            this.OnHome();
        }
    }

    /* navigation */

    private _parent = [ { scroll: 0, product: null } ];

    get hasParent(){
        return (this._parent[0].product != null);
    }

    OnBack(){
        if (this.hasParent){
            let _prev = this._parent.shift();

            this._viewproducts = null;
            this.change.markForCheck();

            setTimeout(() => {
                this._container.nativeElement.scrollTop = _prev.scroll;
            }, 0);
        }    
    }

    OnHome(){
        this._parent = [ { scroll: 0, product: null } ];

        this._viewproducts = null;
        this.change.markForCheck();
    }

    /* thumbnail actions */

    OnSelect(_product){
        // navigate to group
        if (_product.isgroup){
            let _next = {
                scroll: this._container.nativeElement.scrollTop,
                product: _product
            };

            this._parent.unshift(_next);

            this._viewproducts = null;
            this.change.markForCheck();

            return;     
        }

        // navigate to product
        if (this._NeedDirects(_product)){
            this.Selected = _product;

            return;     
        }

        // cannot navigate: check product
        this.OnCheck(_product);
    }

    private _OnCheckProduct(_product: Product){
        if (this._NeedOptions(_product)){
            this.Selected = _product;
        }
        else {
            let _checkeditem = null;
            for (let _item of this._selected){
                if ((_item.product == _product)){
                    _checkeditem = _item;
                }
            }

            if (_checkeditem){
                this._selected.delete(_checkeditem);
            }
            else {  // not found
                this._selected.add(new SelectedItem(this._CreateItemOptions(_product, []) || _product))
            }
        }   
    }

    private _OnCheckDirect(_product: ProductSelect){
        let _checkeditem = null;
        for (let _item of this._selected){
            if ((_item.direct == _product)){
                _checkeditem = _item;
            }
        }

        if (_checkeditem){
            this._selected.delete(_checkeditem);
        }
        else {  // not found
            // Para impresoras el atajo se ENVUELVE en un PrintProduct (item=select),
            // como los productos (que pasan por _CreatePrintProduct): _ApplyPrinterProducts
            // espera PrintProduct. Para familias se añade el ProductSelect crudo (la
            // familia lo envuelve en su propio OnAddProducts).
            if (this._print !== null){
                let _pp = new PrintProduct(null, this.data);
                _pp.select = _product;
                _pp.status = 'AC';
                this._selected.add(new SelectedItem(_pp));
            }
            else {
                this._selected.add(new SelectedItem(_product));
            }
        }
    }

    OnCheck(_product: Product | ProductSelect){
        if (_product instanceof Product){
            this._OnCheckProduct(_product);
        }

        if (_product instanceof ProductSelect){
            this._OnCheckDirect(_product);
        }

        this._pristine = false;
        this.change.markForCheck();    
    }

    /********************************/
    /* SELECT DIRECTS               */
    /********************************/

    // En impresora, un atajo SIN opciones válidas no sirve: TicketProduct.ActiveDirect
    // lo salta (ticketproduct.ts:543), así que nunca casaría una línea y la impresora
    // no imprimiría nada. Lo excluimos al ofrecerlo. (Familias: sin este filtro extra.)
    private _directUsable(_direct){
        if (!_direct.IsValid){
            return false;
        }
        if (this._print !== null){
            return _direct.options.filter(o => o.IsValid).length > 0;
        }
        return true;
    }

    private _NeedDirects(_product: Product){
        // Mostrar atajos al crear familias Y al configurar impresoras (para poder
        // asignar un atajo concreto a la impresora, no solo el producto entero).
        if ((this._family !== null) || (this._print !== null)){
            return _product.selects.some(_direct => this._directUsable(_direct));
        }

        return false;
    }

    ProductDirects(_product){
        let _directs = [];

        for (let _direct of _product.selects){
            if (this._directUsable(_direct)){
                _directs.push(_direct);
            }
        }

        return _directs;
    }

    OnCancelDirects(){
        this.Selected = null;
    }

    /********************************/
    /* SELECT OPTIONS               */
    /********************************/

    private _NeedOptions(_product: Product){
        if (this._offer !== null){
            return (_product.categories.length > 0);
        }

        return false;   // do not choose options
    }

    private _selectedoptions = new Map();

    IsSelectOption(_option){
        return this._selectedoptions.get(_option);
    }

    OnSelectOption(_option){
        let _next = !this._selectedoptions.get(_option);
        this._selectedoptions.set(_option, _next);

        this.change.markForCheck();
    }

    private _selectall = false;
    get IsSelectAll(){
        return this._selectall;
    }

    OnSelectAll(){
        this._selectall = !this._selectall;
        for (let _key of this._selectedoptions.keys()){
            this._selectedoptions.set(_key, this._selectall);
        }

        this.change.markForCheck();
    }

    ShowCategory(_category){
        if (_category.depends.length == 0){
            return true;    // category has no depends
        }

        // at least one depend must be selected
        return _category.depends.some(
        _depend => {
            return this._selectedoptions.get(_depend.option) 
        });
    }

    OnCancelOptions(){
        this.Selected = null;
    }

    OnRemoveOptions(){
        let _selecteditem = null;
        for (let _item of this._selected){
            if (_item.product == this.Selected){
                _selecteditem = _item;
            }
        }

        this._selected.delete(_selecteditem);
        this.Selected = null;
    }

    OnAcceptOptions(){
        let _included = [];
        for (let _key of this._selectedoptions.keys()){
            if (this._selectedoptions.get(_key)){
                _included.push(_key);
            }
        }

        let _selecteditem = null;
        for (let _item of this._selected){
            if (_item.product == this.Selected){
                _selecteditem = _item;
            }
        }

        // keep the current item: the options must be updated, not created again
        let _currentitem = _selecteditem ? _selecteditem.item : null;

        if (_selecteditem != null){
            this._selected.delete(_selecteditem);
        }

        _selecteditem = this._CreateItemOptions(this.Selected, _included, _currentitem);
        if (_selecteditem){
            this._selected.add(new SelectedItem(_selecteditem));
        }

        this.Selected = null;
    }

    /* the item already selected for this product (never a plain product) */
    private _SelectedItemOf(product): SuitableItems {
        for(let _item of this._selected){
            if (_item.product == product){
                if ((_item.item instanceof Product) || (_item.item instanceof ProductSelect)){
                    return null;    // no options container for this item
                }
                return _item.item;
            }
        }

        return null;
    }

    /* create item options based in input values */
    private _CreateItemOptions(product, _included, _current: SuitableItems = null){
        let _itemoptions = this._SelectedItemOf(product) || _current;

        if (this._offer !== null){
            _itemoptions = this._CreateOfferProduct(product, _itemoptions, _included);
        }

        if (this._extra !== null){
            _itemoptions = this._CreateExtraProduct(product, _itemoptions, _included);          
        }

        if (this._print !== null){
            _itemoptions = this._CreatePrintProduct(product, _itemoptions, _included);          
        }

        return _itemoptions;
    }

    private _CreateOfferProduct(product, offerproduct, includedoptions){
        let _offerproduct = offerproduct;
        if (!_offerproduct){
            _offerproduct = new OfferProduct(null, this.data);
        }

        // do not include invalid options
        let _options = _offerproduct.options.slice(0);
        for (let _option of _options){
            if (!_option.IsValid){
                _offerproduct.DelOption(_option);
            }
        }

        // add every included option
        for (let _offeroption of includedoptions){
            let _found = null;
            for (let _option of _offerproduct.options){
                if (_option.prodopt == _offeroption){
                    _found = _option;
                }
            }

            _found = _found || new OfferProductOption(null, this.data);
            if (_found){
                _found.status = 'AC';
                _found.product = _offerproduct;
                _found.prodopt = _offeroption;
            }

            _offerproduct.AddOption(_found);
        }

        // remove not included options
        for (let _option of _offerproduct.options){
            if (!includedoptions.includes(_option.prodopt)){
                _option.status = 'DE'
            }
        }

        _offerproduct.product = product;
        _offerproduct.status = 'AC';

        return _offerproduct;
    }

    private _CreateExtraProduct(product, extraproduct, includedoptions){
        let _extraproduct = extraproduct;
        if (!_extraproduct){
            _extraproduct = new ExtraProduct(null, this.data);
        }

        // do not include invalid options
        let _options = _extraproduct.options.slice(0);
        for (let _option of _options){
            if (!_option.IsValid){
                _extraproduct.DelOption(_option);
            }
        }

        // add every included option
        for (let _offeroption of includedoptions){
            let _found = null;
            for (let _option of _extraproduct.options){
                if (_option.prodopt == _offeroption){
                    _found = _option;
                }
            }

            _found = _found || new ExtraProductOption(null, this.data);
            if (_found){
                _found.status = 'AC';
                _found.product = _extraproduct;
                _found.prodopt = _offeroption;
            }

            _extraproduct.AddOption(_found);
        }

        // remove not included options
        for (let _option of _extraproduct.options){
            if (!includedoptions.includes(_option.prodopt)){
                _option.status = 'DE'
            }
        }

        _extraproduct.product = product;
        _extraproduct.status = 'AC';

        return _extraproduct;
    }

    private _CreatePrintProduct(product, printproduct, includedoptions){
        let _printproduct = printproduct;
        if (!_printproduct){
            _printproduct = new PrintProduct(null, this.data);
        }

        // do not include invalid options
        let _options = _printproduct.options.slice(0);
        for (let _option of _options){
            if (!_option.IsValid){
                _printproduct.DelOption(_option);
            }
        }

        // add every included option
        for (let _offeroption of includedoptions){
            let _found = null;
            for (let _option of _printproduct.options){
                if (_option.prodopt == _offeroption){
                    _found = _option;
                }
            }

            _found = _found || new PrintProductOption(null, this.data);
            if (_found){
                _found.status = 'AC';
                _found.product = _printproduct;
                _found.prodopt = _offeroption;
            }

            _printproduct.AddOption(_found);
        }

        // remove not included options
        for (let _option of _printproduct.options){
            if (!includedoptions.includes(_option.prodopt)){
                _option.status = 'DE'
            }
        }

        _printproduct.product = product;
        _printproduct.status = 'AC';

        return _printproduct;
    }
}